import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { axiosInstance } from "../../utils/axiosInstance";

export interface RoleCondition {
  fieldName: string;
  operator: "equal" | "not equal" | string;
  values: string[];
  andOrOperator: "AND" | "OR" | null;
  sequenceNo: number;
}

// Interfaces
export interface CreateRoleState {
  appId: number;
  roleName: string;
  objectId?: string;
  sailpointRoleName: string;
  description: string;
  status: string;
  isAutoApprove: any;
  roleConditions: RoleCondition[];
}

export interface CloneRoleData {
  isClone: boolean;
  is_active: boolean;
  sourceRoleId: number;
}

export interface EditRoleState {
  roleId: any;
  appId: any;
  roleName: string;
  objectId?: string;
  sailpointRoleName: string;
  description: string;
  status: string;
  isAutoApprove: any;
  roleConditions: RoleCondition[];
}

export interface NamesData {
  regions?: Record<string, any[]>;
  fleets?: Record<string, any[]>;
  dealers?: Record<string, any[]>;
  distributions?: Record<string, any[]>;
  plants?: Record<string, any[]>;
  lifecycleStatuses?: Record<string, any[]>;
  roleConditions?: RoleCondition[];
  roleName: string;
  objectId: string;
  sailpointRoleName: string;
  description: string;
  status?: string;
  isAutoApprove: any;
  isClone?: boolean;
  is_active?: boolean;
  sourceRoleId?: number;
}

export interface EditApiRelationPayload {
  id: string;
  methodType: string;
  access: string;
  appId: number;
  description: string;
  apiName: string;
  endpointPath: string;
}

export interface CreateFeatureRelationPayload {
  roleId: number;
  featureId: number;
  oldFeatureId?: number;
  permissionIds: any[];
  associatedGroup: string[];
}

export interface DeleteFeatureRelationPayload {
  roleId: number;
  featureId: number;
}

export interface CreateApiRelationPayload {
  id: string;
  methodType: string;
  access: string;
  appId: number;
  roleId: number;
}

export interface Region {
  id: number;
  region_name: string;
}
export interface Fleet {
  id: number;
  fleet_name: string;
}
export interface Dealer {
  id: number;
  dealer_name: string;
}
export interface Distribution {
  id: number;
  distribution_name: string;
}
export interface LifecycleStatuse {
  id: number;
  lifecycle_status_name: string;
}
export interface Plant {
  id: number;
  plant_name: string;
}

export interface RolesDropdownData {
  regions: Region[];
  fleets: Fleet[];
  dealers: Dealer[];
  distributions: Distribution[];
  plants: Plant[];
  lifecycleStatuses: LifecycleStatuse[];
}

export interface InitialRoleState {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  data: CreateRoleState | null;
  currentRoleId: number | null;
  editApiRelationsPayload: EditApiRelationPayload | null;
  saveApiRelationsPayload: EditApiRelationPayload | null;
  createApiRelationsPayload: CreateApiRelationPayload | null;
  namesData: NamesData | null;
  editData: EditRoleState | null;
  featureRelations: {
    createEditPayload: CreateFeatureRelationPayload | null;
    deletePayload: DeleteFeatureRelationPayload | null;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
  };
  rolesDropdownData: RolesDropdownData | {};
  activateButtonDisable: boolean;
  cloneRoleData: CloneRoleData | null;
}

// Async thunks
export const createRole = createAsyncThunk<CreateRoleState, CreateRoleState>(
  "createRole/fetchData",
  async (data, { rejectWithValue }) => {
    try {
      // Strip objectId from payload if it has no real value
      const { objectId, ...rest } = data;
      const payload =
        objectId !== undefined &&
        objectId !== null &&
        objectId !== "" &&
        objectId !== "undefined"
          ? { ...rest, objectId }
          : rest;
      const response = await axiosInstance.post("/rolesetup/role", payload);
      if (response?.data?.error) {
        return response.data;
      }
      return response.data.data as CreateRoleState;
    } catch (error: any) {
      return rejectWithValue(error.response || "Failed to create role");
    }
  },
);

export const editRole = createAsyncThunk<EditRoleState, EditRoleState>(
  "createRole/editRole",
  async (data, { rejectWithValue }) => {
    const { roleId, appId, objectId, ...rest } = data;
    // Strip objectId from payload if it has no real value
    const payload =
      objectId !== undefined &&
      objectId !== null &&
      objectId !== "" &&
      objectId !== "undefined"
        ? { ...rest, objectId }
        : rest;
    try {
      const response = await axiosInstance.put(
        `/rolesetup/edit/role/${roleId}`,
        payload,
      );
      return response.data.data as EditRoleState;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to edit role");
    }
  },
);

export const editApiRelation = createAsyncThunk<any, EditApiRelationPayload>(
  "createRole/editApiRelation",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put("/datasetup/api-relation", data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to edit API relation",
      );
    }
  },
);

export const createApiRelation = createAsyncThunk<
  any,
  CreateApiRelationPayload
>("createRole/createApiRelation", async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post("/datasetup/api-relation", data);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to create API relation",
    );
  }
});

export const deleteApiRelation = createAsyncThunk<
  any,
  string[],
  { rejectValue: string }
>("createRole/deleteApiRelation", async (ids, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put("/datasetup/delete/api-relation", {
      ids,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to delete API relation(s)",
    );
  }
});

export const roleStatusUpdate = createAsyncThunk<
  CreateRoleState,
  { roleId: number; status: string }
>(
  "createRole/roleStatusUpdate",
  async ({ roleId, status }, { rejectWithValue }) => {
    try {
      const newStatus = status === "draft" ? "active" : "draft";
      const response = await axiosInstance.post("/rolesetup/update/status", {
        roleId,
        status: newStatus,
      });
      return response.data as CreateRoleState;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update role status",
      );
    }
  },
);

export const verifyRole = createAsyncThunk<any, number>(
  "createRole/verifyRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/rolesetup/verify-role/${roleId}`,
      );
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to verify role");
    }
  },
);

export const createFeatureRelation = createAsyncThunk<
  any,
  CreateFeatureRelationPayload
>("createRole/createEditFeatureRelation", async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      "/rolesetup/update/feature",
      data,
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to edit Feature relation",
    );
  }
});

export const deleteFeatureRelation = createAsyncThunk<
  any,
  DeleteFeatureRelationPayload
>("createRole/DeleteFeatureRelation", async (data, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete("/rolesetup/feature-relation", {
      data,
    });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to edit Feature relation",
    );
  }
});

// Initial State
const initialState: InitialRoleState = {
  status: "idle",
  error: null,
  data: null,
  currentRoleId: null,
  editApiRelationsPayload: null,
  saveApiRelationsPayload: null,
  createApiRelationsPayload: null,
  namesData: null,
  editData: null,
  featureRelations: {
    createEditPayload: null,
    deletePayload: null,
    status: "idle",
    error: null,
  },
  rolesDropdownData: {},
  activateButtonDisable: true,
  cloneRoleData: null,
};

// Slice
const createRoleSlice = createSlice({
  name: "createRole",
  initialState,
  reducers: {
    resetCreateRole: (state) => {
      state.status = "idle";
      state.error = null;
      state.data = null;
      state.currentRoleId = null;
    },
    updateCreateRole: (
      state,
      action: PayloadAction<CreateRoleState | null>,
    ) => {
      state.data = action.payload;
    },
    setCurrentRoleId: (state, action: PayloadAction<number | null>) => {
      state.currentRoleId = action.payload;
    },
    setActivateButtonDisable: (state, action: PayloadAction<boolean>) => {
      state.activateButtonDisable = action.payload;
    },
    updateEditApiRelationsPayload: (
      state,
      action: PayloadAction<EditApiRelationPayload>,
    ) => {
      state.editApiRelationsPayload = action.payload;
    },
    saveApiRelationsPayload: (
      state,
      action: PayloadAction<EditApiRelationPayload>,
    ) => {
      state.saveApiRelationsPayload = action.payload;
    },
    updateCreateApiRelationsPayload: (
      state,
      action: PayloadAction<CreateApiRelationPayload>,
    ) => {
      state.createApiRelationsPayload = action.payload;
    },
    updateNamesData: (state, action: PayloadAction<NamesData | null>) => {
      state.namesData = action.payload;
    },
    updatecloneRoleData: (
      state,
      action: PayloadAction<CloneRoleData | null>,
    ) => {
      state.cloneRoleData = action.payload;
    },
    featureRelationsCreateEditPayload: (
      state,
      action: PayloadAction<CreateFeatureRelationPayload>,
    ) => {
      state.featureRelations.createEditPayload = action.payload;
    },
    featureRelationsDeletePayload: (
      state,
      action: PayloadAction<DeleteFeatureRelationPayload>,
    ) => {
      state.featureRelations.deletePayload = action.payload;
    },
    updateFeatureRelationStatus: (state, action: PayloadAction<string>) => {
      state.featureRelations.status = "idle";
    },
    updateRolesDropdownData: (
      state,
      action: PayloadAction<RolesDropdownData | {}>,
    ) => {
      state.rolesDropdownData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createRole.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to create role";
      })

      .addCase(editRole.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editRole.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.editData = action.payload;
      })
      .addCase(editRole.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to edit role";
      })

      .addCase(roleStatusUpdate.pending, (state) => {
        state.status = "loading";
      })
      .addCase(roleStatusUpdate.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(roleStatusUpdate.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to update role status";
      })

      .addCase(verifyRole.pending, (state) => {
        state.status = "loading";
      })
      .addCase(verifyRole.fulfilled, (state, action) => {
        state.status = "succeeded";
        // state.data = action.payload;
      })
      .addCase(verifyRole.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to verify role";
      })

      .addCase(editApiRelation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editApiRelation.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(editApiRelation.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to edit API relation";
      })

      .addCase(createApiRelation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(createApiRelation.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.createApiRelationsPayload = action.meta.arg;
      })
      .addCase(createApiRelation.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to create API relation";
      })

      .addCase(deleteApiRelation.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteApiRelation.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(deleteApiRelation.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to delete API relation(s)";
      })
      .addCase(createFeatureRelation.pending, (state) => {
        state.featureRelations.status = "loading";
      })
      .addCase(createFeatureRelation.fulfilled, (state) => {
        state.featureRelations.status = "succeeded";
      })
      .addCase(createFeatureRelation.rejected, (state, action) => {
        state.status = "failed";
        state.featureRelations.error =
          action.error.message || "Failed to create Feature relation";
      })
      .addCase(deleteFeatureRelation.fulfilled, (state) => {
        state.featureRelations.status = "succeeded";
      })
      .addCase(deleteFeatureRelation.rejected, (state, action) => {
        state.status = "failed";
        state.featureRelations.error =
          action.error.message || "Failed to delete Feature relation";
      });
  },
});

// Exports
export const {
  resetCreateRole,
  updateCreateRole,
  setCurrentRoleId,
  updateEditApiRelationsPayload,
  saveApiRelationsPayload,
  updateNamesData,
  updateCreateApiRelationsPayload,
  featureRelationsCreateEditPayload,
  featureRelationsDeletePayload,
  updateFeatureRelationStatus,
  updateRolesDropdownData,
  setActivateButtonDisable,
  updatecloneRoleData,
} = createRoleSlice.actions;

export const selectCreateRole = (state: RootState) => state.createRole;
export const selectNamesData = (state: RootState) => state.createRole.namesData;
export const selectCurrentRoleId = (state: RootState) =>
  state.createRole.currentRoleId;
export const selectcloneRoleData = (state: RootState) =>
  state.createRole.cloneRoleData;
export const selectedRolesDropdownData = (state: RootState) =>
  state.createRole.rolesDropdownData;
export const selectActivateButtonDisable = (state: RootState) =>
  state.createRole.activateButtonDisable;

export default createRoleSlice.reducer;
